<template>
  <Carousel
    v-if="eyecatches.length > 0"
    v-bind="carouselConfig"
    :mouse-drag="isDisplayingMultipleImages"
    :touch-drag="isDisplayingMultipleImages"
    @init="initSlide"
    @slide-end="slideEnd"
  >
    <Slide v-for="(eyecatch, index) in eyecatches" :key="index" :style="slidesStyle[index]">
      <div class="carousel__item">
        <common-image
          :image-object="eyecatch"
          :alt="eyecatch.alt"
          :is-fixed-dimension="true"
          :is-eyecatch="true"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :fetchpriority="index === 0 ? 'high' : 'low'"
          class="eyecatch"
          @mounted="(url: string) => handleImageMounted(index, url)"
        />

        <div v-if="checkMjSelect(event) && index === 0" class="img-stamp">
          <common-lazy-load-image
            :image-url="'/img/stamp_mj-select.svg'"
            :image-object="{ width: 60, height: 60 }"
            :is-fixed-dimension="true"
            alt="mj-select"
            background-color="none"
          />
        </div>
      </div>
    </Slide>
    <template #addons="{ currentSlide, slidesCount }">
      <template v-if="isDisplayingMultipleImages">
        <Navigation>
          <template #prev>
            <div class="nav-button__wrapper">
              <span class="nav-button__icon">
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <path fill="currentColor" d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
                </svg>
              </span>
            </div>
          </template>
          <template #next>
            <div class="nav-button__wrapper">
              <span class="nav-button__icon">
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </span>
            </div>
          </template>
        </Navigation>
        <div class="pagination">{{ `${clamp(currentSlide + 1, 1, slidesCount)}/${slidesCount}` }}</div>
      </template>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-if="bannerStamp" :class="bannerStamp.class" class="banner-stamp" v-html="sanitizeXssHtml(bannerStamp.text)"></div>
    </template>
  </Carousel>
</template>

<script setup lang="ts">
import { Slide, Navigation, Carousel } from 'vue3-carousel'
import 'vue3-carousel/dist/carousel.css'
import sanitizeXssHtml from 'xss'
import { useMainStore } from '@/store'
import GTM from '@/utils/tracking/gtm'
import { EVENT_DETAIL_CAROUSEL_BANNER_VIEW } from '@/utils/constants/gaCustomEventSet'

const FIRST_VIEW_BANNER_INDEX = 0

interface Image {
  alt: string
  sp_url: string
  url: string
  sp_width?: number
  sp_height?: number
  width: number
  height: number
}

function isImage(obj: unknown): obj is Image {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return (
    typeof o.alt === 'string' &&
    typeof o.sp_url === 'string' &&
    typeof o.url === 'string' &&
    (typeof o.sp_width === 'number' || o.sp_width === undefined) &&
    (typeof o.sp_height === 'number' || o.sp_height === undefined) &&
    typeof o.width === 'number' &&
    typeof o.height === 'number'
  )
}

interface Content {
  category_text: string
  sections?: Section[]
}

interface Section {
  image: Image
}

interface FeatureTag {
  slug: string
}

interface Event {
  event_eyecatches?: unknown[]
  detail?: {
    contents?: Content[]
  }
  use_content_image_as_eyecatch: boolean
  feature_tags?: FeatureTag[]
  eyecatch_image: Image
}

interface Props {
  event: Event
  bannerStamp?: {
    class: string[]
    text: string
  }
}

interface SlideEndProps {
  currentSlideIndex: number
  prevSlideIndex: number
  slidesCount: number
}

const props = defineProps<Props>()

function handleImageMounted(index: number, url: string) {
  const webpUrl = getWebpImageSrc(url) || url
  slidesStyle.value[index].backgroundImage = `url(${webpUrl})`
}

function clamp(value: number, min: number, max: number) {
  if (value > max) return max
  if (value < min) return min
  return value
}

function preloadMainImage(images: Image[]) {
  if (images.length === 0) return
  const mainImageUrl = getWebpImageSrc(store.isSP && images[0].sp_url ? images[0].sp_url : images[0].url)
  useHead({
    link: [
      {
        rel: 'preload',
        href: mainImageUrl,
        as: 'image',
        type: mainImageUrl.endsWith('.webp') ? 'image/webp' : 'image/jpeg',
        fetchpriority: 'high'
      }
    ]
  })
}

function checkMjSelect(event: Event) {
  if (!event) return
  return event.feature_tags && event.feature_tags.find(tag => tag.slug === 'mj_select')
}

const validEventEyecatches = computed<Image[]>(() => {
  return (props.event.event_eyecatches || []).filter(isImage)
})

const eyecatches = computed<Image[]>(() => {
  if (validEventEyecatches.value.length > 0) {
    return [props.event.eyecatch_image].concat(validEventEyecatches.value)
  }
  if (props.event.use_content_image_as_eyecatch && props.event.detail?.contents) {
    const targetCategories = ['ポイント', 'お申し込み状況']
    const content_images = props.event.detail.contents
      .filter(content => targetCategories.includes(content.category_text))
      .flatMap(content => content.sections?.map(section => section.image) || [])
      .filter(Boolean)
    return [props.event.eyecatch_image].concat(content_images)
  }
  return [props.event.eyecatch_image]
})

const slidesStyle = ref(eyecatches.value.map((_eyecatch: unknown) => ({ backgroundImage: 'none' })))

const isDisplayingMultipleImages = computed(() => eyecatches.value.length > 1)

const carouselConfig = {
  itemsToShow: 1,
  wrapAround: true
}

const store = useMainStore()
const mainEyecatch = computed(() => props.event.eyecatch_image)
const width = computed(() => (store.isSP && mainEyecatch.value.sp_width ? mainEyecatch.value.sp_width : mainEyecatch.value.width))
const height = computed(() => (store.isSP && mainEyecatch.value.sp_height ? mainEyecatch.value.sp_height : mainEyecatch.value.height))

const slideGTMEventExecute = (index: number) => {
  GTM.sendEventToGa({ ...EVENT_DETAIL_CAROUSEL_BANNER_VIEW, label: { view_banner: index + 1, max_banner: eyecatches.value.length } })
}
const initSlide = () => {
  slideGTMEventExecute(FIRST_VIEW_BANNER_INDEX)
}
const slideEnd = (data: SlideEndProps) => {
  slideGTMEventExecute(data.currentSlideIndex)
}

if (eyecatches.value.length > 0) preloadMainImage(eyecatches.value)
</script>

<style lang="scss" scoped>
.pagination {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  padding-left: 6px;
  padding-right: 6px;
  background-color: $black-1a;
  opacity: 70%;
  color: white;
  border-radius: 40px;
}

.carousel {
  --background-base-color: #{$black-1a};
  --vc-nav-color: white;
  --vc-nav-background: rgba(from var(--background-base-color) r g b / 70%);
  --vc-nav-height: 64px;
  --vc-nav-color-hover: #bfbfbf;
  --vc-nav-width: 23px;
}

.carousel__slide {
  background-size: cover;
  aspect-ratio: v-bind(width) / calc(v-bind(height) * 1.3);
}

:deep(.carousel__prev),
:deep(.carousel__next) {
  margin: 0;
  min-width: 60px;
  height: calc(100% - 32px);
  display: flex;
  align-items: center;
  background: transparent;
}

:deep(.carousel__prev) {
  padding: 0 8px 0 0;
  justify-content: flex-start;
}

:deep(.carousel__next) {
  padding: 0 0 0 8px;
  justify-content: flex-end;
}

.nav-button__wrapper {
  width: 23px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-button__icon {
  background: rgba(from var(--background-base-color) r g b / 70%);
  color: white;
  font-size: 1.8rem;
  font-weight: bold;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-button__icon:hover {
  color: #bfbfbf;
}

.carousel__item {
  width: 100%;
  height: 100%;
  backdrop-filter: blur(20px);
  align-content: center;
  background-color: #d1d1d1b2;
}

:deep(.eyecatch) {
  height: 100%;
  width: 100%;
  object-fit: contain;
}

.banner-stamp {
  position: absolute;
  font-weight: bold;
  color: white;
  padding-left: 16px;
  padding-right: 16px;
  top: 0;

  @include sp {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &.orange {
    background-color: $orange;
  }
}

.img-stamp {
  position: absolute;
  bottom: 10px;
  left: 4px;
  width: 60px;
  height: 60px;
  z-index: 1;
}
</style>
